/**
 * Tavily 搜索适配器（T12a）。
 *
 * 协议：`POST {baseUrl}/search`，请求体 `{ query, max_results }`，
 * 鉴权走 `Authorization: Bearer <apiKey>`。响应形如：
 * ```json
 * { "results": [ { "title": "...", "url": "...", "content": "...", "published_date": "..." } ] }
 * ```
 *
 * **不新增一套 HTTP 基础设施**：超时、重试、外部取消、错误规范化全部复用
 * `src/core/providers/http.ts`（`resolveDeps` / `openResponseWithRetry` / `readJsonBody` /
 * `toProviderError` / `clamp*`），只补两个搜索特有的点：
 * 1. `searchUrl()`——搜索后端不一定带 `/v1`（Tavily 就没有），因此不走 `normalizeBaseUrl`；
 * 2. `parseTavilyResults()`——对脏数据（缺字段 / 类型不符 / 非数组）必须「不崩」。
 *
 * 取消语义：与 T03 的 `chat()`（静默结束迭代）不同，`search()` 返回 Promise，
 * 取消时**拒绝**一个 `SearchError{ code: 'aborted' }`，避免上层把「取消」误当成「零结果」。
 */

import {
  clampRetries,
  clampRetryDelayMs,
  clampTimeoutMs,
  jsonHeaders,
  openResponseWithRetry,
  ProviderHttpError,
  readJsonBody,
  resolveDeps,
  toProviderError,
} from '../providers/http';
import type { FetchLike } from '../providers/types';
import {
  SearchError,
  type ResolvedSearchConfig,
  type SearchDeps,
  type SearchProvider,
  type SearchRequest,
  type SearchResult,
} from './types';

export const TAVILY_KIND = 'tavily' as const;

/** Tavily 官方端点；配置里留空时使用它。 */
export const TAVILY_DEFAULT_BASE_URL = 'https://api.tavily.com';

/** 未显式指定 `limit` 时的默认条数。 */
export const TAVILY_DEFAULT_LIMIT = 5;

/** Tavily `max_results` 上限（超出会被服务端拒绝，这里主动钳制）。 */
export const TAVILY_MAX_LIMIT = 20;

/** `ProviderHttpError.code` 与 `SearchErrorCode` 取值一一对应，可直接搬运。 */
function toSearchError(error: unknown): SearchError {
  if (error instanceof SearchError) return error;
  const normalized = toProviderError(error);
  return new SearchError(normalized.message, normalized.code, normalized.status);
}

/** 把 `limit` 钳制到 `[1, TAVILY_MAX_LIMIT]` 的整数；非法值回落到默认条数。 */
export function clampSearchLimit(
  limit: number | undefined,
  fallback = TAVILY_DEFAULT_LIMIT,
): number {
  if (limit === undefined || !Number.isFinite(limit)) return fallback;
  const floored = Math.floor(limit);
  if (floored < 1) return 1;
  return Math.min(floored, TAVILY_MAX_LIMIT);
}

/**
 * 拼接搜索地址：去掉 baseUrl 尾部斜杠后追加 `/search`。
 *
 * 必须走 `URL` 拼接而不是字符串相加：baseUrl 可能自带 query / hash。
 * 与 `providers/http.ts` 的 `appendPath` 唯一区别是**不补 `/v1`**；
 * 另外做了幂等处理——baseUrl 已经指向 `/search` 时不重复追加。
 */
export function searchUrl(baseUrl: string): string {
  const trimmed = (baseUrl ?? '').trim();
  if (!trimmed) {
    throw new SearchError('baseUrl 为空', 'config');
  }
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    throw new SearchError(`baseUrl 非法: ${baseUrl}`, 'config');
  }
  const pathname = parsed.pathname.replace(/\/+$/, '');
  parsed.pathname = pathname.endsWith('/search') ? pathname : `${pathname}/search`;
  return parsed.toString();
}

interface TavilyRawResult {
  title?: unknown;
  url?: unknown;
  content?: unknown;
  published_date?: unknown;
  publishedDate?: unknown;
}

function toText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * 解析 Tavily 响应体为 `SearchResult[]`。
 *
 * 脏数据容忍规则（回归重点，避免外部返回异常直接崩掉整条对话）：
 * - 整体不是对象 / `results` 不是数组 -> 返回 `[]`；
 * - 单条不是对象 -> 跳过；
 * - `title` / `url` / `content` 缺字段或非字符串 -> 补空串（保持条目存在，不丢引用序号）；
 * - `published_date`（或驼峰 `publishedDate`）非字符串 / 空串 -> 不带该字段。
 */
export function parseTavilyResults(json: unknown): SearchResult[] {
  if (!json || typeof json !== 'object') return [];
  const results = (json as { results?: unknown }).results;
  if (!Array.isArray(results)) return [];

  const out: SearchResult[] = [];
  for (const raw of results) {
    if (!raw || typeof raw !== 'object') continue;
    const item = raw as TavilyRawResult;
    const published = toText(item.published_date) || toText(item.publishedDate);
    const result: SearchResult = {
      title: toText(item.title),
      url: toText(item.url),
      snippet: toText(item.content),
    };
    if (published) result.publishedAt = published;
    out.push(result);
  }
  return out;
}

export class TavilySearchProvider implements SearchProvider {
  readonly id: string;
  readonly kind = TAVILY_KIND;
  private readonly config: ResolvedSearchConfig;
  private readonly url: string;
  private readonly deps: {
    fetch: FetchLike;
    timeoutMs: number;
    retries: number;
    retryDelayMs: number;
    extraHeaders: Record<string, string>;
  };

  constructor(config: ResolvedSearchConfig, deps?: SearchDeps) {
    const resolved = resolveDeps(deps);
    this.config = config;
    this.id = config.id;
    // 空 baseUrl 回落到 Tavily 官方端点；非法地址由 searchUrl 抛 config 错误。
    this.url = searchUrl(config.baseUrl || TAVILY_DEFAULT_BASE_URL);
    // 配置值优先，但一律过边界校验（负数 / NaN 不得绕过钳制）。
    this.deps = {
      fetch: resolved.fetch,
      timeoutMs: clampTimeoutMs(config.timeoutMs, resolved.timeoutMs),
      retries: clampRetries(config.retries, resolved.retries),
      retryDelayMs: clampRetryDelayMs(config.retryDelayMs, resolved.retryDelayMs),
      // 自定义头：配置 headers 覆盖 deps.extraHeaders。
      extraHeaders: { ...resolved.extraHeaders, ...(config.headers ?? {}) },
    };
  }

  async search(req: SearchRequest): Promise<SearchResult[]> {
    // 已取消：不发起任何请求，直接以 aborted 拒绝。
    if (req.signal?.aborted) {
      throw new SearchError('搜索请求已被取消', 'aborted');
    }
    const query = (req.query ?? '').trim();
    if (!query) {
      throw new SearchError('搜索关键词为空', 'config');
    }

    const body = {
      query,
      max_results: clampSearchLimit(req.limit),
    };

    try {
      const { response, guard } = await openResponseWithRetry({
        url: this.url,
        init: {
          method: 'POST',
          headers: jsonHeaders(this.config.apiKey, this.deps.extraHeaders),
          body: JSON.stringify(body),
        },
        deps: {
          fetch: this.deps.fetch,
          timeoutMs: this.deps.timeoutMs,
          retries: this.deps.retries,
          retryDelayMs: this.deps.retryDelayMs,
          extraHeaders: this.deps.extraHeaders,
        },
        externalSignal: req.signal,
      });

      try {
        const json = await readJsonBody<unknown>(response);
        return parseTavilyResults(json);
      } finally {
        guard.dispose();
      }
    } catch (error) {
      if (
        req.signal?.aborted &&
        !(error instanceof ProviderHttpError && error.code === 'timeout')
      ) {
        throw new SearchError('搜索请求已被取消', 'aborted');
      }
      throw toSearchError(error);
    }
  }
}

/** 工厂：供 `SearchRegistry.registerDefaults()` 与自定义注册复用。 */
export function createTavilyProvider(
  config: ResolvedSearchConfig,
  deps?: SearchDeps,
): SearchProvider {
  return new TavilySearchProvider(config, deps);
}
